<template>
  <table class="table table-sm float-right small text-nowrap text-right font-weight-bolder"
                :class="{ 'font-lg': $store.state.largeFont, 'label-outline': outlineLabels }"
                :style="tableStyle">
    <!-- 단위는 라벨 옆에 붙이지 않는다. 카드 상단 오른쪽의 TankUnitBadges 가 한 번만 보여준다 -->
    <!-- 탱크용량(실용량, 탱크 제원). 상단 버튼 그룹에서 옮겨 맨 윗행에 둔다 -->
    <tr v-if="tank">
      <td style="min-width:50px">탱크용량</td>
      <td style="min-width:70px"><CBadge class="val-badge" color="dark">{{ trans(tank.tankRealVol, 'v', volUnit) }}</CBadge></td>
    </tr>
    <tr>
      <td style="min-width:50px">측정유량</td>
      <td style="min-width:70px"> <CBadge class="val-badge" :color="ovmVariant">{{ trans(pk.ovm, 'v', volUnit) }}</CBadge> </td>
    </tr>
    <tr>
      <td>환산유량</td>
      <td><CBadge class="val-badge" color="primary">{{ trans(pk.ovc,'v', volUnit) }}</CBadge></td>
    </tr>
    <tr>
      <td>보정유량</td>
      <td><CBadge class="val-badge" color="secondary">{{ trans(pk.ovcr,'v', volUnit) }}</CBadge></td>
    </tr>
    <tr v-if="showVacancy">
      <td>입고가능량</td>
      <td><CBadge class="val-badge" color="info">{{ trans(pk.vf,'v', volUnit) }}</CBadge></td>
    </tr>
    <tr>
      <td>수분량</td>
      <td><CBadge class="val-badge" color="secondary"> {{ trans(pk.wv, 'v', volUnit) }}</CBadge></td>
    </tr>
    <tr>
      <td>유위</td>
      <td><CBadge class="val-badge" :color="ohrVariant">{{ trans(pk.ohr,'l',lenUnit) }}</CBadge></td>
    </tr>
    <tr>
      <td>수위</td>
      <td><CBadge class="val-badge" :color="wtrVariant">{{ trans(pk.whr,'l',lenUnit) }}</CBadge> </td>
    </tr>
    <!-- 평균온도 라벨을 누르면 온도센서 표가 펼쳐진다 (종전의 온도센서/단위변경 버튼 대체) -->
    <tr>
      <td class="detail-toggle" v-b-tooltip.hover title="클릭: 온도센서 표 펼치기/닫기" @click="$emit('toggle-detail')">평균온도 ▾</td>
      <td><CBadge class="val-badge" color="secondary"> {{ trans(pk.avgTm, 't', tmpUnit) }} </CBadge></td>
    </tr>
  </table>
</template>

<style scoped>
/* 표가 탱크 그림 위에 레이어로 겹칠 때 라벨이 묻히지 않도록 글자에 외곽선을 준다.
   라이트 모드는 어두운 글자에 흰 외곽선, 다크 모드는 밝은 글자에 어두운 외곽선 */
.label-outline td:first-child {
  text-shadow: -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff,
               0 -1px 0 #fff, 0 1px 0 #fff, -1px 0 0 #fff, 1px 0 0 #fff;
}
.c-dark-theme .label-outline td:first-child {
  text-shadow: -1px -1px 0 #1e2125, 1px -1px 0 #1e2125, -1px 1px 0 #1e2125, 1px 1px 0 #1e2125,
               0 -1px 0 #1e2125, 0 1px 0 #1e2125, -1px 0 0 #1e2125, 1px 0 0 #1e2125;
}
.detail-toggle {
  cursor: pointer;
  user-select: none;
  text-decoration: underline dotted;
}
/* 수치 배지는 라벨보다 한 단계 크게. 표가 .small(80%) 이라 rem 으로 고정한다 */
.val-badge {
  font-size: 0.95rem;
  padding: 0.25em 0.45em;
}
.font-lg .val-badge {
  font-size: 1.15rem;
}
</style>

<script>
/**
 * 탱크 상세값 표 (용량, 측정·환산·보정유량, 입고가능량, 수분량, 유위·수위, 평균온도)
 *
 * Tank2D / Tank3D / Tank3DV2 / TankLorry 에 같은 표가 복사돼 있던 것을 하나로 모은 것이다.
 * 총용량 행은 상단 용량 버튼과 중복이라 뺐고, 단위 표시는 TankUnitBadges 로 옮겼다.
 *
 * 값 계산은 하지 않는다. 패킷(pk)과 표시단위를 받아 그리기만 하고,
 * 평균온도 라벨 클릭은 호출측이 처리하도록 @toggle-detail 로 올린다. (단위 전환은 상단 단위 배지가 담당)
 */
import {trans} from '@/common/utils';

/*
  뱃지 색. 네 컴포넌트의 colors.text 가 모두 같은 값이라 여기에 둔다.

  종전 맵의 키는 norm/warn/info 였는데 호출측이 status 에 넣는 값은
  off(초기) / on(정상) / warn(경보·에러) 라서, 정상 상태에서 undefined 가 되어
  의도한 색이 나오지 않았다. 실제로 쓰는 키에 맞춘다.
  off 는 데이터를 받기 전이므로 종전과 같은 기본색(secondary)으로 둔다.
*/
const TEXT_VARIANT = {
  wtr: { off: 'secondary', on: 'secondary', warn: 'primary', info: 'info' },
};

// 주요 수치는 통신 상태와 무관하게 늘 색을 입혀 한눈에 구분되게 한다.
// (경보/에러 상태(warn)일 때만 danger 로 덮는다)
//   측정유량 success, 환산유량 primary, 입고가능량 info, 유위 warning
const FIXED_VARIANT = { ovm: 'success', ohr: 'warning' };

export default {
  name: 'TankDetailTable',
  props: {
    // 현재 패킷
    pk: {type: Object, required: true},
    // 탱크 제원. 용량(tankRealVol) 행에 쓴다
    tank: {type: Object, default: null},
    // 통신/경보 상태 ('off' | 'on' | 'warn')
    status: {type: String, default: 'off'},
    // 표시 단위
    volUnit: {type: String, default: 'l'},
    lenUnit: {type: String, default: 'mm'},
    tmpUnit: {type: String, default: 'C'},
    // 입고가능량 행 표시 여부
    showVacancy: {type: Boolean, default: false},
    // 라벨 글자 외곽선 (탱크 그림 위에 레이어로 겹칠 때)
    outlineLabels: {type: Boolean, default: false},
    // 표 너비. 빈 값이면 지정하지 않는다 (급유차)
    tableWidth: {type: String, default: '120px'},
  },
  computed: {
    tableStyle(){
      return this.tableWidth ? {width: this.tableWidth} : null;
    },

    ovmVariant(){
      return this.status === 'warn' ? 'danger' : FIXED_VARIANT.ovm;
    },

    ohrVariant(){
      return this.status === 'warn' ? 'danger' : FIXED_VARIANT.ohr;
    },

    wtrVariant(){
      return TEXT_VARIANT.wtr[this.status];
    },
  },
  methods: {
    trans,
  },
}
</script>
